import { useApolloClient, type DocumentNode } from '@apollo/client'
import { captureException } from '@sentry/react'
import {
  cloneElement,
  Suspense,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorBoundaryOverlay } from '@/components/shared/error-boundary-overlay'

type QueryErrorBoundaryProps = {
  queries: DocumentNode[]
  fallback: ReactNode
  onReset?: () => void
  children: ReactElement | ((resetKey: number) => ReactNode)
}

export const QueryErrorBoundary = ({
  queries,
  fallback,
  onReset,
  children,
}: QueryErrorBoundaryProps) => {
  const [resetKey, setResetKey] = useState(0)
  const client = useApolloClient()

  const content =
    typeof children === 'function'
      ? children(resetKey)
      : cloneElement(children, { key: resetKey })

  return (
    <ErrorBoundary
      fallback={<ErrorBoundaryOverlay>{fallback}</ErrorBoundaryOverlay>}
      onReset={() => {
        onReset?.()
        client.refetchQueries({ include: queries })
        setResetKey((k) => k + 1)
      }}
      onError={(e) => captureException(e)}
    >
      <Suspense fallback={fallback}>{content}</Suspense>
    </ErrorBoundary>
  )
}
